import { useEffect, useInsertionEffect, useRef } from 'react'
import { useTabScope } from '../workspace/context'
import { useBoardState, useSelectedProject } from '../store/context'

export const APP_NAME = 'Poulpe'

/** Project pages distinguish their tabs only when the workspace has several projects. */
export function useProjectTitle(title: string) {
  const { projects } = useBoardState()
  const project = useSelectedProject()
  useDocumentTitle(project && projects.size > 1 ? `${title} · ${project.name}` : title)
}

/** What names the document now, the last to arrive on top: a page, and whatever is drawn over it. */
const named: { title: string | null | undefined }[] = []

function show() {
  const title = named.at(-1)?.title
  document.title = title ? `${title} · ${APP_NAME}` : APP_NAME
}

/**
 * Names the document for as long as its caller is drawn. When the caller
 * goes, the title goes back to what is still drawn under it, or to the app's
 * own name when nothing is: a page that names nothing never keeps the title
 * of the one before it, and what closes over a page gives the page its title
 * back.
 */
export function useDocumentTitle(title: string | null | undefined) {
  const scope = useTabScope()
  const token = useRef(Symbol('tab title')).current
  useInsertionEffect(() => {
    if (!scope) return
    scope.title(token, title ?? null)
    return () => scope.title(token, undefined)
  }, [scope, title, token])
  const mine = useRef<{ title: string | null | undefined } | null>(null)
  useEffect(() => {
    if (scope) return
    const entry = { title: undefined as string | null | undefined }
    mine.current = entry
    named.push(entry)
    return () => {
      named.splice(named.indexOf(entry), 1)
      show()
    }
  }, [scope])
  useEffect(() => {
    if (scope) return
    if (!mine.current) return
    mine.current.title = title
    show()
  }, [title, scope])
}
